/**
 * <media-gallery> - dependency-free product media gallery.
 *
 * Navigation is native scroll-snap (no slider library). Which slide is
 * "active" is tracked by IntersectionObserver, and every public API takes
 * a Shopify media id rather than a numeric index, so there is no index/DOM
 * offset math to keep in sync anywhere (variant switching, thumbnails, or
 * arrow navigation).
 */
class MediaGallery extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.viewer = this.querySelector('.media-gallery__viewer');
    this.list = this.querySelector('.media-gallery__list');
    this.items = Array.from(this.querySelectorAll('.media-gallery__item'));
    this.prevBtn = this.querySelector('[data-gallery-prev]');
    this.nextBtn = this.querySelector('[data-gallery-next]');
    this.dots = Array.from(this.querySelectorAll('[data-gallery-dot]'));
    this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
    this.thumbTrack = this.querySelector('[data-gallery-thumbs]');
    this.activeMediaId = this.dataset.initialMediaId;
    this.mobileMql = window.matchMedia('(max-width: 989px)');

    if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.step(-1));
    if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.step(1));
    this.dots.forEach((dot) => dot.addEventListener('click', () => this.setActiveMedia(dot.dataset.mediaId)));
    this.thumbs.forEach((thumb) => thumb.addEventListener('click', () => this.setActiveMedia(thumb.dataset.mediaId)));

    this.mobileMql.addEventListener('change', () => this.setupObserver());

    this.setupObserver();
    this.setupZoom();
    this.setupDragToScroll(this.list);
    this.setupDragToScroll(this.thumbTrack);

   if (this.activeMediaId) this.setActiveMedia(this.activeMediaId, { instant: true, skipScroll: true });
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }

  isSliderMode() {
    return this.mobileMql.matches || this.dataset.desktopLayout === 'slider';
  }

  getItem(mediaId) {
    return this.items.find((item) => item.dataset.mediaId === String(mediaId));
  }

 setActiveMedia(mediaId, { instant = false, skipScroll = false } = {}) {
    const item = this.getItem(mediaId);
    if (!item) return;

    this.activeMediaId = String(mediaId);

   if (this.isSliderMode() && !skipScroll) {
      item.scrollIntoView({
        behavior: instant ? 'auto' : 'smooth',
        block: 'nearest',
        inline: 'start',
      });
    } else {
      const stickyHeader = document.querySelector('sticky-header');
      const offset = stickyHeader ? stickyHeader.offsetHeight : 0;
      const top = item.getBoundingClientRect().top + window.pageYOffset - offset - 16;
      if (!instant) window.scrollTo({ top, behavior: 'smooth' });
    }

    this.updateActiveState();
  }

  step(direction) {
    const currentIndex = this.items.findIndex((item) => item.dataset.mediaId === this.activeMediaId);
    let nextIndex = currentIndex + direction;
    if (nextIndex < 0) nextIndex = this.items.length - 1;
    if (nextIndex >= this.items.length) nextIndex = 0;
    this.setActiveMedia(this.items[nextIndex].dataset.mediaId);
  }

  updateActiveState() {
    const id = this.activeMediaId;

    this.dots.forEach((dot) => {
      dot.setAttribute('aria-current', String(dot.dataset.mediaId === id));
    });

    this.thumbs.forEach((thumb) => {
      const isActive = thumb.dataset.mediaId === id;
      thumb.setAttribute('aria-current', String(isActive));
      if (isActive) this.scrollThumbIntoView(thumb);
    });
  }

  scrollThumbIntoView(thumb) {
    const track = this.thumbTrack;
    if (!track) return;

    const trackRect = track.getBoundingClientRect();
    const thumbRect = thumb.getBoundingClientRect();

    if (thumbRect.left < trackRect.left) {
      track.scrollBy({ left: thumbRect.left - trackRect.left - 12, behavior: 'smooth' });
    } else if (thumbRect.right > trackRect.right) {
      track.scrollBy({ left: thumbRect.right - trackRect.right + 12, behavior: 'smooth' });
    }
  }

  setupObserver() {
    if (this.observer) this.observer.disconnect();
    if (!this.list) return;

    // In slider mode the viewer itself scrolls, so intersection must be
    // measured against it; in stacked desktop mode the page scrolls instead.
    const root = this.isSliderMode() ? this.list : null;

    this.observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];

        if (mostVisible) {
          const mediaId = mostVisible.target.dataset.mediaId;
          if (mediaId !== this.activeMediaId) {
            this.activeMediaId = mediaId;
            this.updateActiveState();
          }
        }
      },
      { root, threshold: [0.5, 0.75, 1] }
    );

    this.items.forEach((item) => this.observer.observe(item));
  }

  setupZoom() {
    if (!window.matchMedia('(min-width: 990px)').matches) return;

    this.querySelectorAll('[data-zoom]').forEach((zoomEl) => {
      const img = zoomEl.querySelector('img');
      if (!img) return;

      const onMove = (event) => {
        if (this.list && this.list.classList.contains('is-dragging')) return;
        const rect = zoomEl.getBoundingClientRect();
        const x = ((event.clientX - rect.left) / rect.width) * 100;
        const y = ((event.clientY - rect.top) / rect.height) * 100;
        zoomEl.style.backgroundPosition = `${x}% ${y}%`;
      };

      zoomEl.addEventListener('mouseenter', () => {
        zoomEl.style.backgroundImage = `url(${img.currentSrc || img.src})`;
        zoomEl.classList.add('is-zoomed');
        zoomEl.addEventListener('mousemove', onMove);
      });

      zoomEl.addEventListener('mouseleave', () => {
        zoomEl.classList.remove('is-zoomed');
        zoomEl.removeEventListener('mousemove', onMove);
        zoomEl.style.backgroundImage = '';
      });
    });
  }

  /**
   * Native overflow-scroll only responds to real touch input (and
   * wheel/trackpad gestures) - browsers don't turn a mouse click-drag into
   * a scroll by default. This adds that "grab and swipe" behavior for
   * mouse/pen input via Pointer Events, while leaving real touch input to
   * the browser's own (smoother) native panning.
   */
  setupDragToScroll(el) {
    if (!el) return;

    const DRAG_THRESHOLD = 5;
    let down = false;
    let dragging = false;
    let startX = 0;
    let startScrollLeft = 0;
    let pointerId = null;

    const onPointerDown = (event) => {
      if (event.pointerType === 'touch') return;
      if (el === this.list && !this.isSliderMode()) return;

      down = true;
      dragging = false;
      startX = event.clientX;
      startScrollLeft = el.scrollLeft;
      pointerId = event.pointerId;
    };

    const onPointerMove = (event) => {
      if (!down || event.pointerId !== pointerId) return;
      const dx = event.clientX - startX;

      // Don't commit to a drag (and don't capture the pointer) until the
      // pointer has actually moved - capturing on every plain click would
      // hijack the click event away from whatever was under the pointer
      // (a thumbnail button, a video's play button, etc).
      if (!dragging) {
        if (Math.abs(dx) < DRAG_THRESHOLD) return;
        dragging = true;
        el.classList.add('is-dragging');
        el.setPointerCapture(pointerId);
      }

      el.scrollLeft = startScrollLeft - dx;
    };

    const endDrag = (event) => {
      if (!down || event.pointerId !== pointerId) return;
      down = false;

      if (dragging) {
        el.classList.remove('is-dragging');
        if (el.hasPointerCapture(pointerId)) el.releasePointerCapture(pointerId);

        // A drag that moved the scroller shouldn't also fire a click on
        // whatever was under the pointer (e.g. a thumbnail button).
        const suppressClick = (clickEvent) => {
          clickEvent.preventDefault();
          clickEvent.stopPropagation();
        };
        el.addEventListener('click', suppressClick, { capture: true, once: true });
      }

      dragging = false;
      pointerId = null;
    };

    el.addEventListener('pointerdown', onPointerDown);
    el.addEventListener('pointermove', onPointerMove);
    el.addEventListener('pointerup', endDrag);
    el.addEventListener('pointercancel', endDrag);
  }
}

customElements.define('media-gallery', MediaGallery);
